import React from "react";

import { useTranslation } from "react-i18next";
import ViewDialog from "@components/Dialog";
import InformationView from "@components/InformationView";

import { getDaysLabel, parseValue } from "@lib/utils/string";
import { Binding, RecurringType } from "@lib/types/binding";
import { Box, Divider } from "@mui/material";
import CustomTabs, {
  TabContent,
  TabGroup,
  TabName,
} from "@components/CustomTab";
import DriverCard from "@components/DriverCard";
import UnitCard from "@components/UnitCard";

interface Props {
  viewBindingDialogOpen: boolean;
  setViewBindingDialogOpen: (open: boolean) => void;
  bindingData?: Binding;
}

const ViewBinding: React.FC<Props> = ({
  viewBindingDialogOpen,
  setViewBindingDialogOpen,
  bindingData,
}) => {
  const { t } = useTranslation();

  const bindingInfo = [
    {
      label: t("user.bindings.bindingType"),
      value: t(`user.bindings.${bindingData?.binding_type}`),
      columnSize: 2,
    },
    ...(bindingData?.driver_id !== null
      ? [
          {
            label: t("user.bindings.driver"),
            value:
              bindingData?.driver_details?.name ||
              t("user.bindings.deletedDriver"),
            columnSize: 2,
          },
        ]
      : []),

    ...(bindingData?.driver_group_id !== null
      ? [
          {
            label: t("user.bindings.driverGroup"),
            value:
              bindingData?.driver_group_details?.name ||
              t("user.bindings.deletedDriverGroup"),
            columnSize: 2,
          },
        ]
      : []),
    {
      label: t("user.bindings.status"),
      value: t(`status.${bindingData?.status}`),
      columnSize: 2,
    },
    ...(bindingData?.unit_id !== null
      ? [
          {
            label: t("user.bindings.unit"),
            value:
              bindingData?.unit_details?.name || t("user.bindings.deletedUnit"),
            columnSize: 2,
          },
        ]
      : []),
    ...(bindingData?.unit_group_id !== null
      ? [
          {
            label: t("user.bindings.unitGroup"),
            value:
              bindingData?.unit_group_details?.name ||
              t("user.bindings.deletedUnitGroup"),
            columnSize: 2,
          },
        ]
      : []),
    {
      label: t("user.bindings.startDate"),
      value: bindingData?.start_datetime
        ? new Date(bindingData?.start_datetime).toLocaleString()
        : "-",
      columnSize: 2,
    },
    {
      label: t("user.bindings.endDate"),
      value: bindingData?.end_datetime
        ? new Date(bindingData?.end_datetime).toLocaleString()
        : "-",
      columnSize: 2,
    },
  ];

  const schedules = bindingData?.schedules
    .map((field) => [
      {
        label:
          field.recurring_type === RecurringType.MONTHLY
            ? t("user.bindings.monthly")
            : t("user.bindings.weekly"),
        value:
          field.recurring_type === RecurringType.MONTHLY
            ? parseValue(field.day_of_month)
            : getDaysLabel(field.days_of_week || []),
        columnSize: 4,
      },
      {
        label: t("user.bindings.startDate"),
        value: field.start_time,
        columnSize: 3,
      },
      {
        label: t("user.bindings.endDate"),
        value: field.end_time,
        columnSize: 3,
      },
    ])
    .flat();

  return (
    <ViewDialog
      title={t("user.bindings.view")}
      isOpen={viewBindingDialogOpen}
      handleClose={() => {
        setViewBindingDialogOpen(false);
      }}
    >
      <InformationView
        title={t("user.bindings.bindingInfo")}
        data={bindingInfo}
        columns={4}
      />

      {schedules && schedules.length > 0 && (
        <>
          <Divider />
          <InformationView title="Schedules" data={schedules} />
        </>
      )}

      {(bindingData?.driver_group_id || bindingData?.unit_group_id) && (
        <Box flex={1} sx={{ borderTop: 1, borderColor: "divider" }}>
          <CustomTabs>
            {bindingData?.driver_group_id && (
              <TabGroup>
                <TabName>{t("user.bindings.drivers")}</TabName>
                <TabContent>
                  <Box
                    flex={1}
                    flexDirection={"column"}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      padding: 2,
                    }}
                  >
                    {bindingData?.driver_group_details &&
                    bindingData.driver_group_details.drivers.length > 0 ? (
                      bindingData.driver_group_details.drivers.map((driver) => (
                        <DriverCard driver={driver} />
                      ))
                    ) : (
                      <Box>{t("user.bindings.noDrivers")}</Box>
                    )}
                  </Box>
                </TabContent>
              </TabGroup>
            )}

            {bindingData?.unit_group_id && (
              <TabGroup>
                <TabName>{t("user.bindings.units")}</TabName>
                <TabContent>
                  <Box
                    flex={1}
                    flexDirection={"column"}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      padding: 2,
                    }}
                  >
                    {bindingData?.unit_group_details &&
                    bindingData.unit_group_details.units.length > 0 ? (
                      bindingData.unit_group_details.units.map((unit) => (
                        <UnitCard unit={unit} />
                      ))
                    ) : (
                      <Box>{t("user.bindings.noUnits")}</Box>
                    )}
                  </Box>
                </TabContent>
              </TabGroup>
            )}
          </CustomTabs>
        </Box>
      )}
    </ViewDialog>
  );
};

export default ViewBinding;
